import type {
  AdminLegalObligationHistoryRow,
  AdminLegalObligationListResponse,
  AdminLegalObligationRow,
  ApiResponse,
  UpdateAdminLegalObligationBody,
} from '@safetyhub/shared';
import api from './api';

/**
 * **법정 의무 카탈로그** API 클라이언트 — 「법정 의무 카탈로그」 페이지(`/legal-obligation-catalog`)가 쓴다.
 *
 * base `/admin/legal-obligations`. 계약 정본은 shared `features/legal-obligation-catalog.ts`와 서버
 * `AdminLegalObligationsController` JSDoc이다. 권한은 `legal-obligation-catalog` 하나(읽기·쓰기 공용)다.
 *
 * ⚠️ 순수 판정(미기재 칸 · 저장 본문 만들기)은 이 파일에 두지 않는다 — 화면 spec이 이 모듈을
 * `vi.mock`하면 팩토리가 준 키만 남아 그 함수까지 사라진다. 그 자리는 `legalObligationCatalogFields.ts`다.
 *
 * ⛔ 적용 조건을 고치는 함수가 **없다** — 서버에 그 입구가 없다(카탈로그 서비스 docblock). 여기에
 * 만들지 말 것: 조건 쓰기는 이력 스냅샷·개정 축 판정과 함께 서버에서 먼저 정해야 하는 별 결정이다.
 */

/** 봉투에서 본문을 꺼낸다 — 정본 타입이 `data: T | null`이라 그 null을 여기서 한 번 가른다(선례 `announcements.ts`) */
function unwrapEnvelope<T>(envelope: ApiResponse<T>): T {
  if (envelope.data === null) {
    throw new Error('서버 응답에 본문이 없습니다. 잠시 후 다시 시도해 주세요.');
  }
  return envelope.data;
}

const BASE = '/admin/legal-obligations';

export const LEGAL_OBLIGATION_CATALOG_QUERY_KEYS = {
  list: ['admin', 'legal-obligations'] as const,
  history: (obligationId: string) => ['admin', 'legal-obligations', obligationId, 'history'] as const,
};

/** 개정 응답 — 서버 `UpdateLegalObligationResult` */
export interface UpdateLegalObligationResponse {
  /** 갱신된 카탈로그 행(날것 — 목록 행 형상과 다르다. 화면은 목록을 다시 읽는다) */
  obligation: { id: string; version: number };
  /** 실제로 달라진 칸 — **비면 아무 일도 하지 않았다**(같은 값을 보냈다 · version 그대로) */
  changedFields: string[];
  historyId: string | null;
}

export const legalObligationCatalogApi = {
  async list(): Promise<AdminLegalObligationRow[]> {
    const res = await api.get<ApiResponse<AdminLegalObligationListResponse>>(BASE);
    return unwrapEnvelope(res.data).obligations;
  },

  async update(
    obligationId: string,
    body: UpdateAdminLegalObligationBody,
  ): Promise<UpdateLegalObligationResponse> {
    const res = await api.patch<ApiResponse<UpdateLegalObligationResponse>>(
      `${BASE}/${obligationId}`,
      body,
    );
    return unwrapEnvelope(res.data);
  },

  async history(obligationId: string): Promise<AdminLegalObligationHistoryRow[]> {
    const res = await api.get<ApiResponse<AdminLegalObligationHistoryRow[]>>(
      `${BASE}/${obligationId}/history`,
    );
    return unwrapEnvelope(res.data);
  },
};
